import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, ArrowUp, CheckCircle2, Loader2, MessageCircle, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";
import { timeAgo } from "@/lib/utils-ros";

const SUGGESTIONS = [
  "Just met head of ops at UltraTech, they want a pilot next month",
  "Reminder — send Reliance CFO the 5-year TCO by Friday",
  "New lead: Aarti Industries, Kavita Nair, safety compliance urgent",
  "Signal from IndiaChem — Reliance downloaded our ESG report, strong intent",
  "New opportunity with JSW Steel, gate 1, safety compliance interest",
  "JSW deal update — CFO wants revised pricing",
];

const WELCOME_MESSAGE = {
  role: "assistant",
  content: "Hey — I'm Niq. Text me like you'd text a colleague. I can add companies, signals, leads, and opportunities, log meetings, and turn follow-ups into tasks.",
  created_at: new Date().toISOString(),
};

export default function Chat() {
  const [sessionId] = useState(() => `niq-${Date.now()}`);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const capturedUpdates = useMemo(() => (
    messages
      .flatMap(message => message.executed || [])
      .filter(update => update.type !== "company_selection_required")
      .slice()
      .reverse()
  ), [messages]);

  async function confirmCompany(pendingId, candidateIndex) {
    try {
      const { data } = await api.post("/chat/confirm-company", {
        pending_id: pendingId,
        candidate_index: candidateIndex,
      });
      setMessages(current => current.map(message => ({
        ...message,
        executed: message.executed?.map(update => (
          update.pending_id === pendingId ? data.update : update
        )),
      })));
      const label = data.update.type.replaceAll("_", " ");
      toast.success(`${label}: ${data.update.name || data.update.title || data.company?.name || "Saved"}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not use that company");
    }
  }

  async function send(text) {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages(current => [...current, { role: "user", content: msg, created_at: new Date().toISOString() }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat/send", { session_id: sessionId, message: msg });
      setMessages(current => [...current, {
        role: "assistant",
        content: data.reply,
        executed: data.executed,
        parsed: data.parsed,
        created_at: new Date().toISOString(),
      }]);
      data.executed?.forEach(update => {
        if (update.type === "company_selection_required") return;
        const skipped = update.type.endsWith("_skipped") || update.type.endsWith("_failed");
        const label = update.type.replaceAll("_", " ");
        if (skipped) {
          toast.error(update.reason || label, { icon: <AlertTriangle size={14} /> });
        } else {
          const detail = update.title || update.name || update.opportunity || "";
          toast.success(detail ? `${label}: ${detail}` : label, {
            icon: <Zap size={14} className="text-[var(--saarv-blue-strong)]" />,
          });
        }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ask Niq could not send that message");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="h-[calc(100vh-106px)] min-h-[620px] flex flex-col bg-gradient-to-b from-[#F3FAFD] via-[#FAFDFE] to-white" data-testid="chat-page">
      <header className="px-5 md:px-8 pt-6 md:pt-8 pb-4 shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#07539A]">Ask Niq</h1>
            <p className="text-[13px] text-[var(--text-muted)] mt-1">Your conversational layer for pipeline updates, meetings, and follow-ups.</p>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-[10px] font-mono text-[var(--text-muted)]">
            <span className="w-2 h-2 rounded-full bg-[var(--success)] ai-pulse" />
            Niq is ready
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 px-4 md:px-8 pb-5 md:pb-8">
        <div className="max-w-7xl mx-auto h-full flex gap-5 md:gap-7">
          <section className="flex-1 min-w-0 rounded-3xl border border-[#D9E7EE] bg-white/65 shadow-[0_16px_40px_rgba(10,73,117,0.08)] overflow-hidden flex flex-col" data-testid="ask-niq-conversation">
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 md:px-8 py-6 md:py-8 space-y-6" data-testid="chat-messages">
              {messages.map((message, index) => (
                <ChatMessage key={`${message.created_at}-${index}`} message={message} onCompanyConfirm={confirmCompany} />
              ))}

              {sending && (
                <div className="flex justify-start" data-testid="ask-niq-typing">
                  <div className="relative rounded-2xl rounded-bl-md border border-[#C9D8DF] bg-white px-4 py-3 shadow-[0_5px_14px_rgba(10,42,67,0.08)] flex items-center gap-2 text-[11px] text-[var(--text-muted)]">
                    <span className="absolute -left-1.5 bottom-2 w-3 h-3 rotate-45 bg-white border-l border-b border-[#C9D8DF]" />
                    <Loader2 size={13} className="animate-spin text-[var(--saarv-blue-strong)]" />
                    Niq is thinking…
                  </div>
                </div>
              )}
            </div>

            <div className="shrink-0 border-t border-[#D9E7EE] bg-white px-4 md:px-6 py-4">
              {messages.length === 1 && (
                <div className="flex gap-2 mb-3 overflow-x-auto pb-1" data-testid="chat-suggestions">
                  {SUGGESTIONS.map((suggestion, index) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => send(suggestion)}
                      data-testid={`chat-suggestion-${index}`}
                      className="shrink-0 max-w-[290px] truncate text-[10px] px-3 py-2 rounded-full bg-[#F4F9FC] border border-[#D5E5ED] hover:border-[var(--saarv-blue)] hover:bg-[var(--saarv-blue-soft)] transition-colors text-left"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex items-end gap-3 rounded-2xl border border-[#C9DCE6] bg-[#F8FCFE] px-4 py-3 shadow-[0_5px_16px_rgba(10,73,117,0.07)] focus-within:border-[var(--saarv-blue)] focus-within:bg-white transition-colors">
                <MessageCircle size={17} className="text-[var(--saarv-blue-strong)] mb-1 shrink-0" />
                <textarea
                  value={input}
                  onChange={event => setInput(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Tell Niq what happened or what needs to get done…"
                  rows={1}
                  data-testid="chat-input"
                  className="flex-1 bg-transparent outline-none resize-none text-[12px] placeholder:text-[var(--text-dim)] max-h-32 py-0.5"
                />
                <button
                  type="button"
                  onClick={() => send()}
                  disabled={sending || !input.trim()}
                  data-testid="chat-send"
                  aria-label="Send message to Ask Niq"
                  className="w-8 h-8 rounded-full bg-[#087CF0] text-[#FFFFFF] disabled:opacity-35 hover:bg-[#0569CE] flex items-center justify-center transition-colors shrink-0"
                >
                  <ArrowUp size={16} />
                </button>
              </div>
            </div>
          </section>

          {capturedUpdates.length > 0 && (
            <aside className="hidden md:flex w-[280px] lg:w-[320px] xl:w-[350px] shrink-0 rounded-3xl border border-[#D6E2E8] border-l-[7px] border-l-[#07539A] bg-white shadow-[0_18px_44px_rgba(10,73,117,0.15)] overflow-hidden flex-col animate-in fade-in slide-in-from-right-3 duration-300" data-testid="live-crm-updates">
              <div className="px-5 py-5 border-b border-[#E1E9ED]">
                <div className="flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-xl bg-[var(--saarv-blue-soft)] text-[#07539A] flex items-center justify-center">
                    <Sparkles size={15} />
                  </span>
                  <div>
                    <h2 className="font-display text-[16px] text-[#07539A]">Live CRM Updates</h2>
                    <p className="text-[9px] text-[var(--text-muted)] mt-0.5">Captured in this conversation</p>
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {capturedUpdates.map((update, index) => (
                  <UpdateCard key={`${update.type}-${update.id || index}-${index}`} update={update} />
                ))}
              </div>
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatMessage({ message, onCompanyConfirm }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[86%] md:max-w-[72%] flex flex-col ${isUser ? "items-end" : "items-start"}`}>
        <div className={`relative rounded-2xl px-4 py-3 ${
          isUser
            ? "rounded-br-md bg-[#087CF0] text-[#FFFFFF] shadow-[0_7px_18px_rgba(8,124,240,0.22)]"
            : "rounded-bl-md bg-white border border-[#C9D8DF] text-[#111827] shadow-[0_6px_16px_rgba(10,42,67,0.09)]"
        }`}>
          <span className={`absolute bottom-2 w-3 h-3 rotate-45 ${
            isUser
              ? "-right-1.5 bg-[#087CF0]"
              : "-left-1.5 bg-white border-l border-b border-[#C9D8DF]"
          }`} />
          <div className="relative z-[1] text-[12px] md:text-[13px] leading-relaxed whitespace-pre-wrap">{message.content}</div>
          {message.executed?.length > 0 && (
            <div className={`relative z-[1] mt-3 pt-2 border-t space-y-1.5 ${isUser ? "border-white/20" : "border-black/10"}`}>
              {message.executed.map((update, index) => {
                if (update.type === "company_selection_required") {
                  return <CompanySelection key={update.pending_id || index} update={update} onConfirm={onCompanyConfirm} />;
                }
                const skipped = update.type.endsWith("_skipped") || update.type.endsWith("_failed");
                return (
                  <div key={`${update.type}-${index}`} className={`flex items-center gap-1.5 text-[9px] ${skipped ? "text-[var(--danger)]" : isUser ? "text-[#FFFFFF]" : "text-[var(--saarv-blue-strong)]"}`}>
                    {skipped ? <AlertTriangle size={11} /> : <CheckCircle2 size={11} />}
                    <span className="uppercase tracking-wider">{update.type.replaceAll("_", " ")}:</span>
                    <span className={isUser ? "text-[#FFFFFF]" : "text-[#111827]"}>
                      {skipped ? update.reason : (update.title || update.name || update.opportunity)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="mt-1.5 px-1 text-[9px] font-mono text-[#82939D]">{timeAgo(message.created_at)}</div>
      </div>
    </div>
  );
}

function CompanySelection({ update, onConfirm }) {
  const [busy, setBusy] = useState(null);

  async function choose(index) {
    setBusy(index);
    try { await onConfirm(update.pending_id, index); }
    finally { setBusy(null); }
  }

  return (
    <div className="space-y-2" data-testid={`company-selection-${update.pending_id}`}>
      <div className="text-[10px] font-semibold text-[#07539A]">Choose the company you meant</div>
      <div className="grid gap-2 max-h-72 overflow-y-auto pr-1">
        {(update.candidates || []).map((company, index) => (
          <button key={`${company.name}-${index}`} type="button" onClick={() => choose(index)} disabled={busy !== null}
            data-testid={`company-candidate-${index}`}
            className="text-left rounded-xl border border-[#C9DCE6] bg-[#F8FCFE] hover:border-[#07539A] hover:bg-[#E7F5FB] p-3 disabled:opacity-50 transition-colors">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-[#12324A]">{company.name}</span>
              <span className="ml-auto text-[8px] uppercase tracking-wider text-[#07539A]">{company.source === "universe" ? "In Universe" : "AI suggestion"}</span>
            </div>
            <div className="text-[9px] text-[#78909C] mt-1">
              {[company.industry, company.sub_industry, company.hq_city, company.state].filter(Boolean).join(" · ") || "Company details unavailable"}
            </div>
            {company.website && <div className="text-[9px] text-[#07539A] mt-1 truncate">{company.website}</div>}
            {company.ai_summary && <div className="text-[9px] text-[#526D7A] mt-1.5 leading-relaxed line-clamp-2">{company.ai_summary}</div>}
            {busy === index && <Loader2 size={12} className="animate-spin text-[#07539A] mt-2" />}
          </button>
        ))}
      </div>
    </div>
  );
}

function UpdateCard({ update }) {
  const title = update.title || update.name || update.opportunity || "CRM updated";
  return (
    <div className="rounded-2xl border border-[#DDE7EC] bg-[#F9FCFD] p-4 shadow-[0_6px_16px_rgba(10,73,117,0.07)]">
      <div className="flex items-start gap-3">
        <span className="w-7 h-7 rounded-full bg-[#E8F7EE] text-[#087B3D] flex items-center justify-center shrink-0">
          <CheckCircle2 size={14} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[9px] uppercase tracking-[0.16em] text-[#07539A] font-medium">{update.type.replaceAll("_", " ")}</div>
          <div className="text-[11px] mt-1 break-words">{title}</div>
          {update.note && <div className="text-[10px] text-[var(--text-muted)] mt-1.5 leading-relaxed">{update.note}</div>}
        </div>
      </div>
    </div>
  );
}
